import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Header from '../../components/Header';
import { api, openUploadDownload } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { VerifyCheckOutButton } from '../../components/equipment/VerifyCheckOutButton';
import { getJobTypeLabel } from '../../constants/job-types';
import { isTransferDestination } from '../../lib/transfer-destination';
import { canReturnToYard } from '../../lib/return-to-yard';
import { formatCalendarDate } from '../../lib/calendar-date';
import {
  ArrowLeft,
  ArrowRightLeft,
  Wrench,
  ClipboardCheck,
  ClipboardList,
  AlertTriangle,
  MapPin,
  Shield,
  Loader2,
  MapPinOff,
  Undo2,
  X,
  Navigation,
} from 'lucide-react';
import { useBack } from '../../hooks/useBack';

/** What the API told us went wrong, or something honest if it did not say. */
function errorText(err: any): string {
  return err?.response?.data?.error || err?.message || 'Unknown error';
}

/**
 * Shared chrome for the three disposition modals — same backdrop, card and
 * dismiss affordance the existing left-on-site modal used, so a hand who has
 * seen one has seen all three.
 */
function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-5 w-full max-w-md space-y-4 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-white">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="text-fieldiq-muted"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const selectClass =
  'w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white';

function LeftOnSiteModal({
  equipment,
  pending,
  onClose,
  onConfirm,
}: {
  equipment: any;
  pending: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError('Please provide a reason');
      return;
    }
    setError('');
    onConfirm(reason.trim());
  };

  return (
    <ModalShell title="Left on Site" onClose={onClose}>
      <p className="text-sm text-fieldiq-text">
        Mark <span className="text-white font-semibold">{equipment.name}</span> as left on site.
      </p>
      <div>
        <label className="text-xs text-fieldiq-muted mb-1 block" htmlFor="left-on-site-reason">Reason *</label>
        <textarea
          id="left-on-site-reason"
          rows={3}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Why is this equipment being left on site?"
          className={`${selectClass} resize-none`}
        />
      </div>
      {error && <p className="text-red-400 text-xs">{error}</p>}
      <button
        onClick={handleSubmit}
        disabled={pending}
        className="w-full bg-amber-500 text-fieldiq-dark font-semibold rounded-lg py-2.5 text-sm min-h-[44px] disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {pending ? <Loader2 size={16} className="animate-spin" /> : <MapPinOff size={16} />}
        Confirm Left on Site
      </button>
    </ModalShell>
  );
}

/**
 * Yard picker for a return. The office Equipment page has its own copy of this
 * (PR #56) but it is inline JSX wired to that screen's state, not a component —
 * nothing to import. The yards themselves come from the same
 * `/service-locations` query key that page uses, so the list is the company's
 * real yards and never a hardcoded set.
 */
function YardPickerModal({
  equipment,
  pending,
  onClose,
  onConfirm,
}: {
  equipment: any;
  pending: boolean;
  onClose: () => void;
  onConfirm: (yardId: string, yardName: string) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['service-locations'],
    queryFn: () => api.serviceLocations.list().then(r => r.data.data ?? r.data ?? []),
  });
  const yards: any[] = Array.isArray(data) ? data : [];
  const homeYardId: string =
    equipment.home_yard_id ?? equipment.homeYardId ?? equipment.current_yard_id ?? equipment.currentYardId ?? '';

  // Pre-select the asset's home yard, but only if that yard is still one of the
  // company's — a stale home yard must not preselect an id the API would
  // reject. Derived rather than seeded into state by an effect, because the
  // yards arrive after the first render and `null` (untouched) is a different
  // thing from `''` (the hand deliberately cleared the field).
  const [picked, setPicked] = useState<string | null>(null);
  const yardId = picked ?? (yards.some(y => y.id === homeYardId) ? homeYardId : '');

  const selected = yards.find(y => y.id === yardId);

  return (
    <ModalShell title="Returned to Yard" onClose={onClose}>
      <p className="text-sm text-fieldiq-text">
        Return <span className="text-white font-semibold">{equipment.name}</span> to a yard.
      </p>
      <div>
        <label className="text-xs text-fieldiq-muted mb-1 block" htmlFor="return-yard">Destination Yard *</label>
        <select id="return-yard" className={selectClass} value={yardId} onChange={e => setPicked(e.target.value)}>
          <option value="">{isLoading ? 'Loading yards...' : 'Select yard...'}</option>
          {yards.map(y => (
            <option key={y.id} value={y.id}>{y.city ? `${y.name} — ${y.city}` : y.name}</option>
          ))}
        </select>
        {!isLoading && yards.length === 0 && (
          <p className="text-amber-400 text-xs mt-2">No yards are set up for your company yet.</p>
        )}
      </div>
      <button
        onClick={() => selected && onConfirm(selected.id, selected.name)}
        disabled={!selected || pending}
        className="w-full bg-green-500 text-white font-semibold rounded-lg py-2.5 text-sm min-h-[44px] disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {pending ? <Loader2 size={16} className="animate-spin" /> : <Undo2 size={16} />}
        Confirm Return
      </button>
    </ModalShell>
  );
}

/**
 * Job picker for a transfer. Only jobs still taking equipment are offered, and
 * never the job the asset is already on.
 *
 * WHICH jobs those are is `isTransferDestination` in lib/transfer-destination.ts
 * — the single client-side mirror of the server's own list — and NOT an inline
 * `status === 'active' || status === 'scheduled'`, which is what stood here.
 * That inline predicate is why this screen had to be found by grep when
 * `on_hold` was added: the office picker (EquipmentScreen) already imported the
 * helper, so it would have gained the new status while this one silently kept
 * refusing to offer a held job, and the two pickers onto one endpoint would
 * have disagreed. A shared constant makes that one edit instead of a search.
 *
 * The reason is optional, unlike the left-on-site one. The destination job is
 * already most of the answer to "why did it move", and a required field on the
 * control a hand reaches for several times a shift is a field that fills up
 * with a single character. It is offered because the API records it on the
 * incoming assignment row, and a transfer that says nothing about itself is
 * how transfers became uncountable in the first place.
 */
function JobPickerModal({
  equipment,
  pending,
  onClose,
  onConfirm,
}: {
  equipment: any;
  pending: boolean;
  onClose: () => void;
  onConfirm: (jobId: string, jobNumber: string, reason: string) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api.jobs.list().then(r => r.data.data ?? r.data ?? []),
  });
  // WHICH JOB TO EXCLUDE, AND WHY THE FALLBACK IS DROPPED FOR AN AT-LOCATION
  // ROW. `assigned_job_id` means two different things depending on the row:
  // on an assignment row it is the job the gear is ON, which must not be
  // offered as its own destination; on an `at_your_location` row it is THIS
  // HAND'S job — the destination he is almost certainly reaching for.
  //
  // Keeping the `?? assigned_job_id` fallback there would filter out the only
  // job he wants, on exactly the rows where the fallback fires: 10 of the 15
  // left-on-site assets in production have `current_job_id` NULL, so this is
  // the common case for them, not an edge. He would open the picker, find his
  // own job missing, and have no way to tell why.
  const currentJobId = isAtYourLocation(equipment)
    ? equipment.current_job_id ?? null
    : equipment.current_job_id ?? equipment.assigned_job_id ?? null;
  const jobs: any[] = (Array.isArray(data) ? data : [])
    .filter(isTransferDestination)
    .filter((j: any) => j.id !== currentJobId);

  const [jobId, setJobId] = useState('');
  const [reason, setReason] = useState('');
  const selected = jobs.find(j => j.id === jobId);
  const numberOf = (j: any) => j.job_number || j.jobNumber || 'Job';

  return (
    <ModalShell title="Transferred to Job" onClose={onClose}>
      <p className="text-sm text-fieldiq-text">
        Move <span className="text-white font-semibold">{equipment.name}</span> to another job.
      </p>
      <div>
        <label className="text-xs text-fieldiq-muted mb-1 block" htmlFor="transfer-job">Destination Job *</label>
        <select id="transfer-job" className={selectClass} value={jobId} onChange={e => setJobId(e.target.value)}>
          <option value="">{isLoading ? 'Loading jobs...' : 'Select job...'}</option>
          {jobs.map(j => {
            const rig = j.rig_name || j.rigName;
            return (
              <option key={j.id} value={j.id}>{rig ? `${numberOf(j)} — ${rig}` : numberOf(j)}</option>
            );
          })}
        </select>
        {!isLoading && jobs.length === 0 && (
          <p className="text-amber-400 text-xs mt-2">No open jobs to transfer to.</p>
        )}
      </div>
      <div>
        <label className="text-xs text-fieldiq-muted mb-1 block" htmlFor="transfer-reason">Reason</label>
        <textarea
          id="transfer-reason"
          rows={2}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Optional — why is it moving?"
          className={`${selectClass} resize-none`}
        />
      </div>
      <button
        onClick={() => selected && onConfirm(selected.id, numberOf(selected), reason.trim())}
        disabled={!selected || pending}
        className="w-full bg-blue-500 text-white font-semibold rounded-lg py-2.5 text-sm min-h-[44px] disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {pending ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightLeft size={16} />}
        Confirm Transfer
      </button>
    </ModalShell>
  );
}

/**
 * The one-tap claim: which job an at-location asset would be claimed ONTO.
 *
 * ─── WHY A TAP AND NOT AUTO-ATTACH ──────────────────────────────────────────
 *
 * Derek asked for gear left at a rig to attach itself to the next job there.
 * It does not, deliberately. Rig-key matching is ambiguous — measured on
 * production 2026-09-09, TNT has THREE live scheduled jobs sharing the rig key
 * PWS1257, and the only tiebreaker the arm-4 CTE has is the LSD, which on those
 * three rows holds "Fort Saskatchewan", "Fort Sask" and "Fort Saskatchewan": a
 * town, spelled two ways. A 1-in-3 guess would be bad enough; a transfer also
 * CLEARS `last_known_rig`, which is the key arm 4 joins on, so a wrong guess
 * makes the asset unfindable by the crew that is actually at the right rig,
 * with nothing to undo it from. See API issue #636.
 *
 * A hand standing at the lease resolves that ambiguity by looking. This turns
 * his confirmation into one tap instead of a scroll through every open job.
 *
 * ─── WHAT `assigned_job_id` MEANS HERE ──────────────────────────────────────
 *
 * On an `at_your_location` row it is THIS HAND'S job — the one he is at the rig
 * FOR — not the job the asset is on. (On an assignment row it means the
 * opposite; JobPickerModal documents the same asymmetry.) So it is exactly the
 * destination, already computed server-side by the arm that found the row.
 *
 * ─── AND IT MUST AGREE WITH THE SERVER ABOUT ELIGIBILITY ────────────────────
 *
 * Arm 4's candidate list includes `pending`; `TRANSFER_DESTINATION_STATUSES`
 * does not, because a job with no date agreed is not a destination. So a hand
 * crewed on a PENDING job at that rig sees the asset and must NOT be offered a
 * one-tap claim the API would 400 — he falls through to the picker, which
 * filters by the same helper. Returning null here is what routes him there.
 */
function claimTarget(eq: any): { jobId: string; jobNumber: string } | null {
  if (!isAtYourLocation(eq)) return null;
  const jobId = eq?.assigned_job_id;
  if (!jobId) return null;
  if (!isTransferDestination({ status: eq?.job_status })) return null;
  return { jobId, jobNumber: eq?.job_number || 'this job' };
}

/**
 * Where the job this equipment is assigned to is being run — what an inspection
 * means by "Job Location". Built only from the job columns the my-equipment row
 * carries; `eq.location` is deliberately absent, because that is the yard the
 * asset lives in, which is not where the tech is standing. Empty when the row
 * says nothing about the job, and the tech types it.
 */
function jobLocationOf(eq: any): string {
  return eq.well_name || eq.well || eq.rig_name || eq.rig || '';
}

/**
 * Rows the API tagged `at_your_location`: gear standing at the rig this hand is
 * crewed at, which nobody assigned to him.
 *
 * THE SOURCE FIELD IS THE ONLY TEST, and re-deriving it from
 * `status === 'left_on_site'` would be wrong in both directions — a hand's OWN
 * left-on-site gear is `assignment` and belongs in his job group, and the API's
 * rule (rig fold, crew membership, pad agreement) is not something a screen can
 * reproduce from the row. One definition, on the side that has the data.
 */
function isAtYourLocation(eq: any): boolean {
  return eq?.source === 'at_your_location';
}

/**
 * The day the gear was left, from the originating job's scheduled date.
 *
 * `formatCalendarDate`, not `formatInstantDay`. `jobs.scheduled_date` is a
 * timestamptz of MIXED encoding — some rows carry a real time of day, some are
 * a calendar day stored as UTC midnight — and CLAUDE.md settles the reading:
 * on every divergent row that has a ticket, `tickets.work_date` backs the UTC
 * day 8 times out of 8 and the tenant-local day 0 times. `formatCalendarDate`
 * takes the UTC date part; rendering it as an instant would move the
 * midnight-encoded rows a day earlier for every Alberta hand looking at it.
 *
 * Null when absent, and the caller renders the absence rather than a
 * substitute.
 */
function leftOnDay(eq: any): string | null {
  return formatCalendarDate(eq?.left_on_job_date, { month: 'short', day: 'numeric' });
}

/**
 * Groups sort by how much they need the hand's attention: work in front of them
 * first, then gear the job has finished with. `needsDisposition` is the bottom
 * bucket and gets its own section header.
 */
function groupRank(jobStatus: string): number {
  if (jobStatus === 'active') return 0;
  if (jobStatus === 'scheduled') return 1;
  if (jobStatus === 'completed') return 3;
  return 2;
}

export default function FieldEquipmentScreen() {
  const navigate = useNavigate();
  const goBack = useBack('/field');
  const queryClient = useQueryClient();
  // The inspection checklists are a casing product, same guard as the equipment
  // detail pages. A tenant without the vertical keeps the pass/fail equipment
  // check — sending them to a casing template would be worse than what they had.
  const { hasVertical } = useAuth();
  const canInspect = hasVertical('casing_running');
  const [certLoading, setCertLoading] = useState<string | null>(null);
  const [leftOnSiteEq, setLeftOnSiteEq] = useState<any>(null);
  const [returnEq, setReturnEq] = useState<any>(null);
  const [transferEq, setTransferEq] = useState<any>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['field-my-equipment'],
    queryFn: async () => {
      const r = await api.employee.myEquipment();
      return r.data?.data ?? r.data ?? [];
    },
  });

  const equipment: any[] = Array.isArray(data) ? data : [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['field-my-equipment'] });

  const returnMutation = useMutation({
    mutationFn: ({ eq, yardId }: { eq: any; yardId: string; yardName: string }) =>
      api.equipment.returnFromSite(eq.id, { destinationYardId: yardId }),
    onSuccess: (_res, vars) => {
      toast.success(`Equipment returned to ${vars.yardName}`);
      setReturnEq(null);
      invalidate();
    },
    onError: (err: any) => toast.error(`Failed to return equipment: ${errorText(err)}`),
  });

  const leftOnSiteMutation = useMutation({
    mutationFn: ({ eq, reason }: { eq: any; reason: string }) =>
      api.equipment.leftOnSite(eq.id, {
        reason,
        jobId: eq.current_job_id || eq.assigned_job_id || null,
      }),
    onSuccess: () => {
      toast.success('Equipment marked as left on site');
      setLeftOnSiteEq(null);
      invalidate();
    },
    onError: (err: any) => toast.error(`Failed to mark equipment: ${errorText(err)}`),
  });

  const transferMutation = useMutation({
    // `reason` is omitted rather than sent empty when the hand did not type one.
    // The API writes the body's reason onto the incoming assignment row, and a
    // blank string there is a note that says nothing while looking like one.
    mutationFn: ({ eq, jobId, reason }: { eq: any; jobId: string; jobNumber: string; reason: string }) =>
      api.equipment.transferToJob(eq.id, {
        destinationJobId: jobId,
        ...(reason ? { reason } : {}),
      }),
    onSuccess: (_res, vars) => {
      toast.success(`Equipment transferred to ${vars.jobNumber}`);
      setTransferEq(null);
      invalidate();
    },
    onError: (err: any) => toast.error(`Failed to transfer equipment: ${errorText(err)}`),
  });

  const handleViewCert = async (equipmentId: string) => {
    setCertLoading(equipmentId);
    try {
      const res = await api.employee.getEquipmentCertUrl(equipmentId);
      const uploadId = res.data.data?.uploadId;
      if (uploadId) {
        void openUploadDownload(uploadId);
      }
    } catch (err) {
      console.error('Failed to get cert URL:', err);
    } finally {
      setCertLoading(null);
    }
  };

  const statusBadge = (status?: string) => {
    if (status === 'left_on_site') return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-semibold">Left on Site</span>
    );
    if (status === 'in_use') return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-semibold">In Use</span>
    );
    if (status === 'maintenance') return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-semibold">Maintenance</span>
    );
    if (status === 'available') return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-semibold">Available</span>
    );
    if (status === 'reserved') return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 font-semibold">Reserved</span>
    );
    return null;
  };

  /**
   * One equipment card. `needsDisposition` is the whole reason completed-job
   * gear shows up here at all: job completion releases reserved equipment on
   * its own, but anything that was in a hand's possession stays assigned until
   * that hand says where it went.
   */
  const renderCard = (eq: any, jobStatus: string) => {
    const isLeftOnSite = eq.status === 'left_on_site';
    const isInUse = eq.status === 'in_use';
    // Gear standing at this hand's rig that nobody assigned to him. It is a
    // different KIND of row, not a different status: the status is
    // left_on_site either way, and what distinguishes it is that the API
    // reached it through the rig rather than through an assignment.
    const atLocation = isAtYourLocation(eq);
    const needsDisposition = jobStatus === 'completed' && !isLeftOnSite;
    // Returning is offered on any gear a hand actually has — in_use or
    // left_on_site — and the job's status is no part of it.
    //
    // This used to read `needsDisposition || isLeftOnSite`, so a hand could not
    // say he had brought gear back until an office user completed the job. TNT
    // reported the consequence: gear physically standing in the shop reads
    // in_use for as long as the paperwork takes. The comment that stood here
    // gave the premise — "while the job is running the hand still has it and
    // has not brought it anywhere" — and that premise is false. Gear comes off
    // a running job and goes home mid-job.
    //
    // Which statuses those are is `canReturnToYard` in lib/return-to-yard.ts,
    // the single client-side mirror of the server's own list, NOT a
    // predicate spelled out again at the call site — the identical mistake
    // `isTransferDestination` was extracted to stop. The office screen imports the same helper, so the two Returns
    // onto one endpoint cannot drift apart again.
    //
    // `needsDisposition` survives: `canTransfer`, the card's amber border and
    // the "job complete — where did this go?" banner all still read it.
    const canDispose = canReturnToYard(eq.status);
    // Transferring is offered whenever the equipment is on a job, running or
    // not. Gear moves lease to lease mid-job — that is ordinary at TNT, not an
    // exception — and the screen used to require a hand to declare it Left on
    // Site first just to reach this button. Every transfer in production went
    // through that step, with "Transfer" typed as the left-on-site reason,
    // which the transfer then erased. `reserved` is deliberately absent: that
    // is gear the office set aside in a yard, and moving it is a dispatch
    // decision rather than a field one.
    const canTransfer = isInUse || isLeftOnSite || needsDisposition;
    // Null when the row is not at-location, carries no job, or the hand's job
    // is one the API would refuse (`pending`). Each of those routes him to the
    // full picker rather than to a button that 400s.
    const claim = claimTarget(eq);
    const location = jobLocationOf(eq);
    const isReturning = returnMutation.isPending && returnMutation.variables?.eq?.id === eq.id;
    const isTransferring = transferMutation.isPending && transferMutation.variables?.eq?.id === eq.id;

    // One definition, rendered in one of two slots above. Two copies of this
    // JSX is how the two orderings come to differ in something other than
    // order.
    const transferButton = (
      <button
        onClick={() => setTransferEq(eq)}
        disabled={isTransferring}
        // FILLED on an at-location row only while it is the primary answer.
        // Once Claim is offered, Claim is the primary answer and this becomes
        // the quiet alternative — two filled blue buttons side by side say
        // nothing about which one the hand should reach for.
        className={`flex-1 py-2 rounded-lg text-sm font-medium min-h-[44px] flex items-center justify-center gap-2 disabled:opacity-50 ${
          atLocation && !claim ? 'bg-blue-500 text-white font-semibold' : 'bg-blue-500/20 text-blue-400'
        }`}
      >
        {isTransferring ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightLeft size={16} />}
        Transferred to Job
      </button>
    );

    return (
      <div
        key={eq.id}
        // Named container. A card is a set of controls about ONE asset, and
        // several cards now offer the same buttons, so "the Transfer button"
        // is ambiguous without saying whose. Naming the group beats reaching
        // for it through the DOM by class name, which couples a test to the
        // styling.
        role="group"
        aria-label={eq.name}
        className={`bg-fieldiq-card rounded-xl p-4 ${
          atLocation ? 'border border-blue-500/40'
            : isLeftOnSite ? 'border border-amber-500/30'
            : needsDisposition ? 'border border-amber-500/50' : ''
        }`}
      >
        {atLocation && (
          <div className="mb-3 -mx-4 -mt-4 px-4 py-2.5 rounded-t-xl bg-blue-500/15 border-b border-blue-500/30 flex items-center gap-2">
            <Navigation size={15} className="text-blue-300 shrink-0" />
            <p className="text-xs font-semibold text-blue-300">Already at this location — not on your job</p>
          </div>
        )}

        {needsDisposition && (
          <div className="mb-3 -mx-4 -mt-4 px-4 py-2.5 rounded-t-xl bg-amber-500/15 border-b border-amber-500/30 flex items-center gap-2">
            <AlertTriangle size={15} className="text-amber-400 shrink-0" />
            <p className="text-xs font-semibold text-amber-400">Job completed — where did this equipment go?</p>
          </div>
        )}

        <button onClick={() => navigate(`/field/equipment/${eq.id}`)} className="w-full text-left">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isLeftOnSite ? 'bg-amber-500/20' : 'bg-fieldiq-accent/20'
              }`}>
                <Wrench size={20} className={isLeftOnSite ? 'text-amber-400' : 'text-fieldiq-accent'} />
              </div>
              <div>
                <h3 className="font-semibold">{eq.name}</h3>
                <p className="text-xs text-fieldiq-muted">{eq.unit_number} · {eq.category}</p>
              </div>
            </div>
            {statusBadge(eq.status)}
          </div>

          {/* Job info. On an at-location row `eq.job_number` is THIS HAND'S
              job — the reason he is standing at the rig — and printing it bare
              as "Job: J-4320" reads as though the gear belongs to it, which is
              the one thing this row means it does not. The provenance block
              below says both, in the right order. */}
          {eq.job_number && !atLocation && (
            <p className="text-xs text-fieldiq-muted mb-2">Job: {eq.job_number}</p>
          )}

          {/* ─── Provenance ────────────────────────────────────────────────
              Where it came from, on the row, because a hand who cannot see
              that has no way to judge whether the gear is his to move. Each
              part is rendered ONLY when the API sent it: 10 of the 15
              left-on-site assets in production have no job link left, so the
              job number and the date are genuinely absent there — and absent
              renders as absent, never as a plausible default. */}
          {atLocation && (
            <div className="mb-2 space-y-1">
              <p className="text-xs text-blue-300 flex items-center gap-1">
                <MapPin size={12} className="shrink-0" />
                {[eq.last_known_rig, eq.last_known_well].filter(Boolean).join(' · ') || 'Location not recorded'}
              </p>
              <p className="text-xs text-fieldiq-muted">
                {eq.left_on_job_number
                  ? <>Left on <span className="text-fieldiq-text">{eq.left_on_job_number}</span>
                      {leftOnDay(eq) ? <> · {leftOnDay(eq)}</> : null}</>
                  : 'Left here — originating job no longer on record'}
              </p>
              {eq.job_number && (
                <p className="text-xs text-fieldiq-muted">You are here for {eq.job_number}</p>
              )}
            </div>
          )}

          {/* Where it is standing — the thing a hand needs to know before going
              to fetch gear that was left behind. */}
          {isLeftOnSite && !atLocation && location && (
            <p className="text-xs text-amber-400 mb-2 flex items-center gap-1">
              <MapPin size={12} /> {location}
            </p>
          )}
        </button>

        {/* Verify & Check Out — a standalone checkout action, not part of the
            inspection. Same component the equipment detail page uses; it owns the
            file picker, the overlay and the first-scan enrolment. */}
        <VerifyCheckOutButton
          equipmentId={eq.id}
          hasReferencePhoto={!!(eq.photoUrl ?? eq.photo_url)}
          onVerified={invalidate}
          buttonClassName="w-full bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-fieldiq-dark font-semibold py-2.5 rounded-lg text-sm min-h-[44px] transition-colors"
          labels={{ matched: 'Check-out authorized', mismatch: 'Equipment mismatch' }}
        />

        {/* Action buttons */}
        <div className="flex gap-2 mt-2">
          {canInspect ? (
            <button
              onClick={() => navigate('/field/safety/inspection', {
                state: {
                  equipmentId: eq.id,
                  equipmentType: eq.type ?? eq.equipment_type ?? eq.category ?? null,
                  equipmentData: {
                    name: eq.name ?? null,
                    asset_number: eq.unit_number ?? null,
                    manufacturer: eq.manufacturer ?? null,
                    model: eq.model ?? null,
                    serial_number: eq.serial_number ?? null,
                    size: eq.size ?? null,
                    capacity: eq.capacity ?? null,
                  },
                  // `assigned_job_id` FIRST, and the order is the whole point.
                  // It is `aj.id` from /employee/my-equipment — the job whose
                  // card this button is on, and the job the hand means. The
                  // equipment's own `current_job_id` is a denormalised pointer
                  // that answers a different question and frequently disagrees:
                  // measured 2026-08-30 over the endpoint's exact row set, 112
                  // rows, `current_job_id` is NULL on 14 and points at a
                  // DIFFERENT job on 17. Preferring it filed ~15% of
                  // inspections against a job the tech did not tap.
                  //
                  // This line read `current_job_id ?? assigned_job_id` until
                  // API #445. It was harmless for exactly as long as the form
                  // threw the value away — the form's own comment said so —
                  // and became a wrong-job filing the moment it was persisted.
                  jobId: eq.assigned_job_id ?? eq.current_job_id ?? null,
                  jobName: eq.job_number ?? null,
                  jobLocation: location,
                },
              })}
              className="flex-1 bg-fieldiq-accent/20 text-fieldiq-accent py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
            >
              <ClipboardList size={16} /> Run Inspection
            </button>
          ) : (
            <button
              onClick={() => navigate('/field/equipment/check', { state: { equipment: eq } })}
              className="flex-1 bg-fieldiq-accent/20 text-fieldiq-accent py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
            >
              <ClipboardCheck size={16} /> Run Check
            </button>
          )}
        </div>

        {/* Disposition. Three ways gear leaves a hand's hands, each on its own
            gate — they used to share one, which is what tied Transfer to
            Return and put it behind Left on Site. Both buttons are set to grow
            into the free space, so a lone one spans the row rather than
            sitting half-width next to a gap. */}
        {(canDispose || canTransfer) && (
          // ─── ORDER AND WEIGHT ARE THE MESSAGE ON AN AT-LOCATION ROW ───────
          //
          // Everywhere else Return and Transfer are peers, side by side and
          // equally weighted, because a hand who HAS the gear could plausibly
          // do either. This row is the case where they are not peers: the
          // asset is standing at the lease he has just driven to, and the
          // answer that costs nothing is picking it up onto the job he is
          // already on. Return means a trip to the yard — which is exactly the
          // trip nobody made, and the reason the gear sat there.
          //
          // So Transfer goes FIRST and FILLED, Return second and quiet. Return
          // is not removed: a hand genuinely may be taking it home, and hiding
          // the option would be the mirror of the bug this whole change fixes.
          //
          // THE ORDER IS DOM ORDER, NOT `flex-row-reverse`. Reversing in CSS
          // leaves a screen reader and keyboard tab order announcing Return
          // first — the opposite of what the sighted layout says — so the
          // emphasis would be a lie to exactly the users who cannot see it.
          // Swapping the elements costs the same and is true in both.
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            {/* CLAIM FIRST, and the picker KEPT behind it rather than replaced.
                One tap is the answer for the hand standing at the lease, and it
                is the whole reason auto-attach was refused — he resolves the
                rig ambiguity by looking. But a hand may genuinely be picking
                the asset up for a DIFFERENT job, and removing the picker to
                make room for the shortcut would be the mirror of the bug this
                screen exists to fix. Same argument the Return button below is
                kept on. */}
            {/* ONE at-location gate, not two. `claimTarget` returns null for an
                assignment row, so an `atLocation &&` here as well would make the
                pair redundant — and with two guards, deleting either alone
                proves nothing. Measured: with both in place, removing the check
                inside claimTarget reddened ZERO of this file's 12 tests. */}
            {canTransfer && claim && (
              <button
                onClick={() => transferMutation.mutate({
                  eq, jobId: claim.jobId, jobNumber: claim.jobNumber, reason: '',
                })}
                disabled={isTransferring}
                className="flex-1 bg-blue-500 text-white py-2 rounded-lg text-sm font-semibold min-h-[44px] flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isTransferring ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightLeft size={16} />}
                Claim for {claim.jobNumber}
              </button>
            )}
            {atLocation && canTransfer && transferButton}
            {canDispose && (
              <button
                onClick={() => setReturnEq(eq)}
                disabled={isReturning}
                className={`flex-1 py-2 rounded-lg text-sm font-medium min-h-[44px] flex items-center justify-center gap-2 disabled:opacity-50 ${
                  atLocation
                    ? 'bg-fieldiq-dark border border-fieldiq-border text-fieldiq-text'
                    : 'bg-green-500/20 text-green-400'
                }`}
              >
                {isReturning ? <Loader2 size={16} className="animate-spin" /> : <Undo2 size={16} />}
                Returned to Yard
              </button>
            )}
            {!atLocation && canTransfer && transferButton}
          </div>
        )}

        {/* Only gear a hand actually has can be left behind — not something
            still sitting available or merely reserved, and not something
            already left on site. */}
        {isInUse && (
          <button
            onClick={() => setLeftOnSiteEq(eq)}
            className="mt-2 w-full bg-amber-500/10 text-amber-400 py-2 rounded-lg text-sm font-medium min-h-[44px] flex items-center justify-center gap-2"
          >
            <MapPinOff size={16} /> Left on Site
          </button>
        )}

        {/* Cert button */}
        {eq.cert_upload_id && (
          <button
            onClick={() => handleViewCert(eq.id)}
            disabled={certLoading === eq.id}
            className="mt-2 w-full bg-green-500/10 text-green-400 px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            {certLoading === eq.id ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
            View Certificate
          </button>
        )}
      </div>
    );
  };

  const renderGroup = (group: { key: string; jobNumber: string; rigName: string; customerName: string; jobType: string; jobStatus: string; items: any[] }) => (
    <div key={group.key}>
      {/* Job header */}
      <div className="mb-2 px-1">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${
            group.jobStatus === 'active' ? 'bg-green-400'
              : group.jobStatus === 'scheduled' ? 'bg-amber-400'
              : group.jobStatus === 'completed' ? 'bg-fieldiq-muted'
              : 'bg-blue-400'
          }`} />
          <h3 className="text-sm font-bold text-white">{group.jobNumber}</h3>
          {group.rigName && <span className="text-xs text-fieldiq-muted">· {group.rigName}</span>}
        </div>
        {group.customerName && (
          <p className="text-xs text-fieldiq-text ml-4">{group.customerName}{group.jobType ? ` · ${group.jobType}` : ''}</p>
        )}
      </div>
      {/* Equipment cards */}
      <div className="space-y-2">
        {group.items.map((eq: any) => renderCard(eq, group.jobStatus))}
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">My Equipment</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4">
        {error ? (
          <div className="text-center py-12 text-red-400">
            <AlertTriangle size={40} className="mx-auto mb-3" />
            <p>Failed to load equipment</p>
            <p className="text-xs mt-1 text-fieldiq-muted">{(error as any)?.message || 'Unknown error'}</p>
          </div>
        ) : isLoading ? (
          <div className="text-center py-12 text-fieldiq-text">Loading equipment...</div>
        ) : equipment.length === 0 ? (
          <div className="text-center py-12 text-fieldiq-text">
            <Wrench size={40} className="mx-auto mb-3 text-fieldiq-muted" />
            <p>No equipment assigned</p>
            <p className="text-xs mt-1">Equipment from your active jobs will appear here</p>
          </div>
        ) : (() => {
          // AT-LOCATION ROWS ARE PULLED OUT BEFORE GROUPING, and that is not a
          // cosmetic choice. They carry `assigned_job_id` = the hand's OWN job,
          // so grouping by that key would file them under his job header —
          // beside the gear that IS on his job, under a heading that says they
          // belong to it. The one thing the row means is that they do not.
          const atLocation = equipment.filter(isAtYourLocation);
          const assigned = equipment.filter((eq: any) => !isAtYourLocation(eq));

          // Group equipment by job
          const byJob: Record<string, { key: string; jobNumber: string; rigName: string; customerName: string; jobType: string; jobStatus: string; items: any[] }> = {};
          assigned.forEach((eq: any) => {
            const key = eq.assigned_job_id || eq.job_number || 'unassigned';
            if (!byJob[key]) {
              byJob[key] = {
                key,
                jobNumber: eq.job_number || 'Unassigned',
                rigName: eq.rig_name || '',
                customerName: eq.customer_name || '',
                // Shared label map, not replace(/_/g, ' '): that rendered
                // `liner_and_tie_back` as "liner and tie back" and would render
                // migration 0322's `push_down_jacks` as "push down jacks".
                // The conditional stays — getJobTypeLabel(null) returns '—',
                // which is truthy, and the render site at the group header
                // prefixes a ' · ' separator for any truthy value.
                jobType: eq.job_type ? getJobTypeLabel(eq.job_type) : '',
                jobStatus: eq.job_status || '',
                items: [],
              };
            }
            byJob[key].items.push(eq);
          });

          // Live work first, finished jobs last. Gear on a completed job is the
          // only thing here that is actively waiting on the hand to do
          // something, so it gets pulled out from under its own header rather
          // than sitting in the run of job groups.
          const groups = Object.values(byJob).sort(
            (a, b) => groupRank(a.jobStatus) - groupRank(b.jobStatus),
          );
          const current = groups.filter(g => groupRank(g.jobStatus) !== 3);
          const pending = groups.filter(g => groupRank(g.jobStatus) === 3);

          // At-location gear is grouped by the RIG it is standing at, not by a
          // job. There is no job that owns it — that is the whole point — and
          // several assets left by different jobs can be standing at one rig.
          const byRig: Record<string, { rig: string; items: any[] }> = {};
          atLocation.forEach((eq: any) => {
            const rig = eq.last_known_rig || eq.rig_name || 'Location not recorded';
            if (!byRig[rig]) byRig[rig] = { rig, items: [] };
            byRig[rig].items.push(eq);
          });
          const rigGroups = Object.values(byRig).sort((a, b) => a.rig.localeCompare(b.rig));

          return (
            <div className="space-y-5">
              {current.map(renderGroup)}

              {/* ─── At Your Location ──────────────────────────────────────
                  Between the hand's own work and the disposition queue, on
                  purpose. Above it is what he is accountable for; below it is
                  what he owes an answer on; this is the third thing — gear he
                  can pick up right now because he is standing next to it. */}
              {rigGroups.length > 0 && (
                // Named region rather than an anonymous div: a test (and a
                // screen reader) needs to say "the At Your Location section"
                // without reaching for a parent chain or a class name, the
                // same reason each card carries role="group" with the asset's
                // name.
                <div role="region" aria-label="At Your Location" className="space-y-4">
                  <div className="pt-4 border-t border-fieldiq-border">
                    <div className="flex items-center gap-2">
                      <Navigation size={16} className="text-blue-300 shrink-0" />
                      <h2 className="text-sm font-bold text-blue-300">At Your Location</h2>
                    </div>
                    <p className="text-xs text-fieldiq-muted mt-1 ml-6">
                      Left on site at a rig you are working. Not assigned to you — transfer it onto
                      your job to take it.
                    </p>
                  </div>
                  {rigGroups.map(group => (
                    <div key={`rig-${group.rig}`}>
                      <div className="mb-2 px-1 flex items-center gap-2">
                        <MapPin size={13} className="text-blue-300 shrink-0" />
                        <h3 className="text-sm font-bold text-white">{group.rig}</h3>
                      </div>
                      <div className="space-y-2">
                        {group.items.map((eq: any) => renderCard(eq, eq.job_status || ''))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {pending.length > 0 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 pt-4 border-t border-fieldiq-border">
                    <AlertTriangle size={16} className="text-amber-400 shrink-0" />
                    <h2 className="text-sm font-bold text-amber-400">Needs Disposition</h2>
                  </div>
                  {pending.map(renderGroup)}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {leftOnSiteEq && (
        <LeftOnSiteModal
          equipment={leftOnSiteEq}
          pending={leftOnSiteMutation.isPending}
          onClose={() => setLeftOnSiteEq(null)}
          onConfirm={reason => leftOnSiteMutation.mutate({ eq: leftOnSiteEq, reason })}
        />
      )}

      {returnEq && (
        <YardPickerModal
          equipment={returnEq}
          pending={returnMutation.isPending}
          onClose={() => setReturnEq(null)}
          onConfirm={(yardId, yardName) => returnMutation.mutate({ eq: returnEq, yardId, yardName })}
        />
      )}

      {transferEq && (
        <JobPickerModal
          equipment={transferEq}
          pending={transferMutation.isPending}
          onClose={() => setTransferEq(null)}
          onConfirm={(jobId, jobNumber, reason) => transferMutation.mutate({ eq: transferEq, jobId, jobNumber, reason })}
        />
      )}
    </div>
  );
}
